import { useMemo } from 'react';
import { Collapse, Empty, Space, Table, Typography } from 'antd';

import SharedProductImage from '../../../components/productImage';
import { formatCurrency, formatKg, formatVolume } from '../../../utils/format';
import './style.less';

const { Text } = Typography;

/** Мөрийн дэвсгэрийн бөөрөнхийлөлт */
const ROW_RADIUS = 8;

/** Барааны зурагны хэмжээ */
const IMAGE_SIZE = 40;

/** Зураг хавтгай харагдахгүйн тулд өгөх зөөлөн сүүдэр (Бараа сонгох модалтай ижил) */
const IMAGE_SHADOW = '0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06)';

/** Барааны нэрийн хамгийн их өргөн (урт бол ellipsis + tooltip) */

/**
 * Төлөв бүрийн бүдэг дэвсгэр — төлвийн badge-ийн өнгөтэй ижил
 * (List/Style.less дэх .delivery-status-* -ийн background)
 */
const STATUS_BG = {
  created: '#e8f7ff',
  delivered: '#ecfdf3',
  canceled: '#fef3f2',
};

/* ─────────── Тусламжийн функцууд ─────────── */

/** Тоо болгож хөрвүүлнэ, болохгүй бол 0 (NaN гарахаас хамгаална) */
const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Баркодуудыг таслалаар нийлүүлнэ, байхгүй бол "-" */
const formatBarcodes = (barcodes) => (barcodes?.length > 0 ? barcodes.join(', ') : '-');

/** Жагсаалтаас сонгосон талбарын нийлбэр */
const sumBy = (list = [], pick) => list.reduce((total, item) => total + num(pick(item)), 0);

/**
 * Мөрийн cell тус бүрд төлвийн дэвсгэрийг тавина. Inline тул antd-ийн hover
 * дэвсгэрээс давуу. Мөр бүр тусдаа "карт" шиг харагдахын тулд эхний/сүүлийн
 * cell-д л радиус авна.
 */
const statusCellProps = (record, isFirst, isLast) => {
  const bg = STATUS_BG[record?.orderStatus];
  if (!bg) return {};

  return {
    style: {
      backgroundColor: bg,
      borderTopLeftRadius: isFirst ? ROW_RADIUS : 0,
      borderBottomLeftRadius: isFirst ? ROW_RADIUS : 0,
      borderTopRightRadius: isLast ? ROW_RADIUS : 0,
      borderBottomRightRadius: isLast ? ROW_RADIUS : 0,
    },
  };
};

/* ─────────── Жижиг компонентууд ─────────── */

/** Барааны зураг: 40x40, preview-гүй */
const ProductImage = ({ src, alt }) => (
  <SharedProductImage
    src={src}
    alt={alt || 'Барааны зураг'}
    size={IMAGE_SIZE}
    style={{ objectFit: 'cover', borderRadius: 4, boxShadow: IMAGE_SHADOW }}
  />
);

/** Хоёр мөр текст: дээр үндсэн, доор нэмэлт (жижиг, сааралдуу) */
const TwoLineText = ({ primary, secondary, align = 'start', danger = false, maxWidth }) => (
  <Space
    direction="vertical"
    size={0}
    style={{ maxWidth, alignItems: align === 'end' ? 'flex-end' : undefined }}
  >
    <Text
      type={danger ? 'danger' : undefined}
      style={{ maxWidth }}
      ellipsis={maxWidth ? { tooltip: primary } : false}
    >
      {primary}
    </Text>
    <Text type="secondary" style={{ fontSize: 12, maxWidth }} ellipsis>
      {secondary}
    </Text>
  </Space>
);

/** Эзэлхүүн — см³. `Math.trunc` нь 1-ээс бага утгыг чимээгүй 0 болгодог байв */
const VolumeText = ({ value }) => (
  <>
    {`${formatVolume(num(value))} см`}
    <sup>3</sup>
  </>
);

/** Барааны зураг + нэр + SKU/баркод */
const ProductCell = ({ image, title, code, danger = false }) => (
  <Space size={12} align="center">
    <ProductImage src={image} alt={title} />
    <TwoLineText danger={danger} primary={title} secondary={code} />
  </Space>
);

/* ─────────── Үндсэн компонент ─────────── */

const ProductTable = ({ products = [], detail = {} }) => {
  /** Хамтарсан бараанд аль хэдийн орсон SKU-г тусдаа жагсаалтаас хасна */
  const filteredProducts = useMemo(() => {
    const groupedSKUs = new Set(
      (detail?.collectiveProducts ?? []).flatMap((collective) =>
        (collective?.groupProducts ?? []).map((product) => product.productSku)
      )
    );

    return groupedSKUs.size > 0
      ? products.filter((product) => !groupedSKUs.has(product.productSKU))
      : products;
  }, [products, detail]);

  /** Эрлиан агуулахын бараанд SKU-ийн оронд баркод харуулна */
  const isErlian = products?.[0]?.requesterCode === 'EHW';

  const columns = useMemo(() => {
    const defs = [
      {
        key: 'index',
        title: '№',
        width: 48,
        align: 'center',
        render: (_, record, index) => <Text type="secondary">{index + 1}</Text>,
      },
      {
        key: 'product',
        title: 'Бараа',
        render: (_, record) => (
          <ProductCell
            danger={record?.orderStatus === 'canceled'}
            image={record?.productImage ?? record?.imageCover}
            title={record?.productTitle ?? record?.title}
            code={
              isErlian
                ? record?.barcodes?.[0]
                : `${record?.productSKU ?? record?.productSku ?? '-'} • ${formatBarcodes(record?.barcodes)}`
            }
          />
        ),
      },
      {
        key: 'total',
        title: 'Дүн / тоо',
        align: 'center',
        width: 140,
        render: (_, record) => (
          <TwoLineText
            align="end"
            primary={record?.totalPrice ? formatCurrency(record.totalPrice) : '-'}
            secondary={`${num(record?.totalUnit ?? record?.stock)} ширхэг`}
          />
        ),
      },
      {
        key: 'weight',
        title: 'Жин / эзэлхүүн',
        align: 'center',
        width: 140,
        render: (_, record) => (
          <TwoLineText
            align="end"
            primary={`${formatKg(record?.totalWeight)} кг`}
            secondary={<VolumeText value={record?.totalVolume} />}
          />
        ),
      },
    ];

    /** Багана бүрд төлвийн дэвсгэрийг залгана */
    return defs.map((col, index) => ({
      ...col,
      onCell: (record) => statusCellProps(record, index === 0, index === defs.length - 1),
    }));
  }, [isErlian]);

  /** Тусдаа бараа + хамтарсан барааны нийт дүн */
  const renderSummary = (rows) => {
    const collectives = detail?.collectiveProducts ?? [];

    const totalPrice =
      sumBy(rows, (row) => row.totalPrice) + sumBy(collectives, (item) => item.totalPrice);
    const totalUnit = sumBy(rows, (row) => row.totalUnit) + sumBy(collectives, (item) => item.unit);
    const totalWeight = sumBy(rows, (row) => row.totalWeight);
    const totalVolume = sumBy(rows, (row) => row.totalVolume);

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell colSpan={2} />
        <Table.Summary.Cell align="center">
          <TwoLineText
            align="end"
            primary={<Text strong>{formatCurrency(totalPrice)}</Text>}
            secondary={`${totalUnit} ширхэг`}
          />
        </Table.Summary.Cell>
        <Table.Summary.Cell align="center">
          <TwoLineText
            align="end"
            primary={<Text strong>{`${formatKg(totalWeight)} кг`}</Text>}
            secondary={<VolumeText value={totalVolume} />}
          />
        </Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  /** Хоёр хүснэгтэд адилхан тохиргоо */
  const tableProps = {
    className: 'product__description__table product__status__table',
    size: 'small',
    columns,
    pagination: false,
    rowKey: '_id',
    scroll: { x: 'max-content' },
  };

  /** Хамтарсан бараа — дотроо дэд бараануудаа агуулна */
  const collapseItems = (detail?.collectiveProducts ?? []).map((product, index) => ({
    key: index,
    style: {
      backgroundColor: STATUS_BG[product?.orderStatus],
      borderRadius: ROW_RADIUS,
      marginBottom: 8,
    },
    label: (
      <Space size="large" style={{ width: '100%', justifyContent: 'space-between' }}>
        <ProductCell
          image={product?.imageCover}
          title={product?.title}
          code={`${product?.productSKU ?? '-'} • ${formatBarcodes(product?.barcodes)}`}
        />
        <TwoLineText
          align="end"
          primary={formatCurrency(product?.totalPrice)}
          secondary={`${num(product?.unit)} ширхэг`}
        />
      </Space>
    ),
    /** Дэд бараанууд — толгой мөрийг давхардуулахгүй */
    children: <Table {...tableProps} showHeader={false} dataSource={product?.groupProducts} />,
  }));

  const rows = detail?.children ?? [];

  if (collapseItems.length === 0 && filteredProducts.length === 0 && rows.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Бараа байхгүй байна."
        style={{ margin: '16px 0' }}
      />
    );
  }

  return (
    <Space direction="vertical" size={12} style={{ width: '100%', padding: '8px 0' }}>
      {collapseItems.length > 0 ? (
        <Collapse ghost expandIconPosition="end" items={collapseItems} />
      ) : null}

      {filteredProducts.length > 0 || rows.length > 0 ? (
        <Table {...tableProps} dataSource={rows} summary={renderSummary} />
      ) : null}
    </Space>
  );
};

export default ProductTable;
